import { t } from "i18next";

import { getElementByUuid } from "./Content";

import type { CountriesState } from "../../reducers/fetch.types";

export interface CountryOption {
  label: string;
  code: string[];
  value: string;
}

export interface AllCountries {
  label: string;
  code: null;
  value: null;
}

export type CountryLabels = Record<string, string>;

interface SelectedCountry {
  uuid: string;
}
export interface SelectedCountryOption {
  value: string;
  country: string;
  label: string;
}

export interface CountryByCode {
  value: string;
  code: string;
  label: string;
  country: string;
  uuid: string;
}

const sortByAlpha = <T extends CountryOption>(countries: T[]): T[] =>
  countries.toSorted((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );

const getCountries = (
  countries: Pick<CountriesState, "data">,
  labels: CountryLabels,
): CountryOption[] =>
  countries.data.reduce<CountryOption[]>(
    (acc, { uuid, country: countryCode }) => {
      const label = labels[countryCode];
      if (label) {
        acc.push({
          value: uuid,
          code: [countryCode],
          label,
        });
      }
      return acc;
    },
    [],
  );

export function getCountryOptions(
  countries: Pick<CountriesState, "data"> | null | undefined,
  getAllCountries: true,
): [AllCountries, ...CountryOption[]];
export function getCountryOptions(
  countries: Pick<CountriesState, "data"> | null | undefined,
  getAllCountries: false,
): CountryOption[];
export function getCountryOptions(
  countries: Pick<CountriesState, "data"> | null | undefined,
  getAllCountries: boolean,
): CountryOption[] | [AllCountries, ...CountryOption[]] {
  if (!countries) return [];

  const labels: CountryLabels = t("languages:countries", {
    returnObjects: true,
  });

  const sortedCountries = sortByAlpha(getCountries(countries, labels));

  if (!getAllCountries) {
    return sortedCountries;
  }

  return [
    {
      label: t("editor-analytics:all_countries"),
      code: null,
      value: null,
    },
    ...sortedCountries,
  ];
}

export const getCountryByCode = (
  countries: CountriesState,
  country: string,
  labels: CountryLabels,
): CountryByCode | null => {
  const countryData = countries.data.find((ctr) => ctr.country === country);

  if (!countryData) return null;

  const label = labels[countryData?.country];

  if (!label) return null;

  return {
    ...countryData,
    value: countryData.uuid,
    code: countryData.country,
    label,
  };
};

export const getCountryLabelMap = (): CountryLabels => {
  const labels: CountryLabels = t("languages:countries", {
    returnObjects: true,
  });
  return labels;
};

export const getSelectedCountries = (
  countries: CountriesState,
  selectedCountries: SelectedCountry[],
): SelectedCountryOption[] => {
  if (!selectedCountries) return [];

  const labels: CountryLabels = t("languages:countries", {
    returnObjects: true,
  });

  const arrayCountries = selectedCountries.reduce<SelectedCountryOption[]>(
    (acc, country) => {
      const countryData = getElementByUuid(country.uuid, countries);
      // Pays absent du store (pas encore chargé) : ignoré plutôt que fatal.
      if (!countryData) return acc;
      const label = labels[countryData.country];

      if (label) {
        acc.push({
          value: countryData.uuid,
          country: countryData.country,
          label,
        });
      }
      return acc;
    },
    [],
  );

  return arrayCountries.toSorted((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );
};
